import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Gamepad2, 
  Mail, 
  Send,
  Shield,
  FileText,
  Users,
  MessageCircle,
  ExternalLink,
  Loader2,
  CheckCircle,
  Monitor,
  ArrowLeftRight,
  Wrench
} from "lucide-react";

import { useStore } from "@/context/StoreContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export const SiteFooter = () => {
  const { settings, categories, telegramUrl } = useStore();
  const navigate = useNavigate();
  const siteTitle = settings?.siteTitle || "CrackOyun";

  // Page modal state
  const [pageModalOpen, setPageModalOpen] = React.useState(false);
  const [pageContent, setPageContent] = React.useState({ title: "", content: "", loading: false });

  // Contact modal state
  const [contactModalOpen, setContactModalOpen] = React.useState(false);
  const [contactForm, setContactForm] = React.useState({ firstName: "", lastName: "", email: "", message: "" });
  const [contactSending, setContactSending] = React.useState(false);
  const [contactSent, setContactSent] = React.useState(false);

  // Filter active categories for footer
  const activeCategories = categories?.filter(c => c.status !== false).slice(0, 8) || [];

  // Open page modal and fetch content
  const openPageModal = async (slug, fallbackTitle) => {
    setPageContent({ title: fallbackTitle, content: "", loading: true });
    setPageModalOpen(true);

    try {
      const res = await fetch(`${API_URL}/api/pages/${slug}`);
      const data = await res.json();
      
      if (data.error) {
        setPageContent({ 
          title: fallbackTitle, 
          content: "Bu sayfa henüz düzenlenmemiş. Admin panelinden içerik ekleyebilirsiniz.", 
          loading: false 
        });
      } else {
        setPageContent({ 
          title: data.title || fallbackTitle, 
          content: data.content || "İçerik bulunamadı.", 
          loading: false 
        });
      }
    } catch {
      setPageContent({ 
        title: fallbackTitle, 
        content: "Sayfa yüklenirken bir hata oluştu.", 
        loading: false 
      });
    }
  };

  // Open contact modal
  const openContactModal = () => {
    setContactForm({ firstName: "", lastName: "", email: "", message: "" });
    setContactSent(false);
    setContactModalOpen(true);
  };

  // Submit contact form
  const submitContact = async () => {
    if (!contactForm.firstName.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
      toast.error("Lütfen gerekli alanları doldurun");
      return;
    }

    setContactSending(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(contactForm),
      });
      const data = await res.json();
      
      if (data.ok) {
        setContactSent(true);
        toast.success("Mesajınız başarıyla gönderildi!");
      } else {
        toast.error(data.error || "Mesaj gönderilemedi");
      }
    } catch {
      toast.error("Bir hata oluştu, lütfen tekrar deneyin");
    } finally {
      setContactSending(false);
    }
  };

  return (
    <>
      <footer className="mt-6 sm:mt-10 rounded-xl sm:rounded-2xl border border-white/10 bg-white/5 overflow-hidden shadow-[0_4px_30px_rgba(0,0,0,0.1)]">
        {/* Main Footer Content */}
        <div className="p-4 sm:p-6 md:p-8">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            
            {/* Brand Section */}
            <div className="col-span-2 sm:col-span-2 lg:col-span-1">
              <Link to="/" className="flex items-center gap-2 sm:gap-3 group mb-3 sm:mb-4">
                {/* Logo Icon - Same as header */}
                <div className="relative">
                  {/* Glow effect behind logo */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-pink-500 to-cyan-500 rounded-xl opacity-0 group-hover:opacity-50 blur-lg transition-all duration-500" />
                  
                  <div className="relative size-10 sm:size-12 rounded-lg sm:rounded-xl overflow-hidden transition-transform duration-300 group-hover:scale-105">
                    {settings?.logo ? (
                      <img 
                        src={settings.logo.startsWith('/') ? `${API_URL}${settings.logo}` : settings.logo}
                        alt={siteTitle}
                        className="relative size-10 sm:size-12 object-contain"
                        loading="lazy"
                        onError={(e) => { 
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    ) : null}
                    <Gamepad2 
                      className={`relative size-8 sm:size-10 text-purple-500 ${settings?.logo ? 'hidden' : ''}`}
                    />
                  </div>
                </div>
                
                {/* Logo Text - With shimmer effect */}
                <span 
                  className="font-extrabold text-base sm:text-lg tracking-tight"
                  style={settings?.siteTitleColor 
                    ? { color: settings.siteTitleColor }
                    : { 
                        background: 'linear-gradient(90deg, rgb(167 139 250), rgb(244 114 182), rgb(56 189 248), rgb(167 139 250))',
                        backgroundSize: '200% auto',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }
                  }
                >
                  {siteTitle}
                </span>
              </Link>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4 leading-relaxed">
                En güncel PC ve mobil oyunları ücretsiz indirin. Hızlı indirme linkleri ve güvenli kurulum rehberleri.
              </p>
              {telegramUrl && (
                <a
                  href={telegramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#0088cc]/10 text-[#0088cc] hover:bg-[#0088cc]/20 transition-colors text-xs sm:text-sm font-medium"
                >
                  <Send className="size-3 sm:size-4" />
                  Telegram Kanalımız
                  <ExternalLink className="size-2.5 sm:size-3" />
                </a>
              )}
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-sm sm:text-base mb-3 sm:mb-4 flex items-center gap-2">
                <FileText className="size-3.5 sm:size-4 text-muted-foreground" />
                Hızlı Linkler
              </h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                <li>
                  <button 
                    onClick={() => openPageModal("about", "Hakkımızda")}
                    className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 sm:gap-2"
                  >
                    <Users className="size-2.5 sm:size-3" />
                    Hakkımızda
                  </button>
                </li>
                <li>
                  <button 
                    onClick={openContactModal}
                    className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 sm:gap-2"
                  >
                    <Mail className="size-2.5 sm:size-3" />
                    İletişim
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => openPageModal("privacy", "Gizlilik Politikası")}
                    className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 sm:gap-2"
                  >
                    <Shield className="size-2.5 sm:size-3" />
                    Gizlilik Politikası
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => openPageModal("terms", "Kullanım Şartları")}
                    className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 sm:gap-2"
                  >
                    <FileText className="size-2.5 sm:size-3" />
                    Kullanım Şartları
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => openPageModal("terms", "DMCA")}
                    className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 sm:gap-2"
                  >
                    <Shield className="size-2.5 sm:size-3" />
                    DMCA
                  </button>
                </li>
              </ul>
            </div>

            {/* Categories */}
            <div>
              <h4 className="font-semibold text-sm sm:text-base mb-3 sm:mb-4 flex items-center gap-2">
                <Gamepad2 className="size-3.5 sm:size-4 text-muted-foreground" />
                Kategoriler
              </h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                {activeCategories.map((cat) => (
                  <li key={cat.id}>
                    <button 
                      onClick={() => {
                        // Scroll to top and dispatch category change event
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        // Navigate to home and trigger category change
                        navigate('/');
                        // Dispatch custom event to change tab
                        setTimeout(() => {
                          window.dispatchEvent(new CustomEvent('footer:category', { detail: cat.id }));
                        }, 100);
                      }}
                      className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                    >
                      {cat.title || cat.name}
                    </button>
                  </li>
                ))}
                {activeCategories.length === 0 && (
                  <>
                    <li><span className="text-muted-foreground">Aksiyon</span></li>
                    <li><span className="text-muted-foreground">Macera</span></li>
                    <li><span className="text-muted-foreground">RPG</span></li>
                    <li><span className="text-muted-foreground">Strateji</span></li>
                  </>
                )}
              </ul>
            </div>

            {/* Tools */}
            <div>
              <h4 className="font-semibold text-sm sm:text-base mb-3 sm:mb-4 flex items-center gap-2">
                <Wrench className="size-3.5 sm:size-4 text-muted-foreground" />
                Araçlar
              </h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                <li>
                  <Link 
                    to="/bilgisayarim-acar-mi"
                    className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 sm:gap-2"
                  >
                    <Monitor className="size-2.5 sm:size-3" />
                    Bilgisayarım Açar Mı?
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/karsilastir"
                    className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 sm:gap-2"
                  >
                    <ArrowLeftRight className="size-2.5 sm:size-3" />
                    Oyun Karşılaştır
                  </Link>
                </li>
                <li>
                  <button 
                    onClick={openContactModal}
                    className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 sm:gap-2"
                  >
                    <MessageCircle className="size-2.5 sm:size-3" />
                    Destek
                  </button>
                </li>
                {telegramUrl && (
                  <li>
                    <a 
                      href={telegramUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 sm:gap-2"
                    >
                      <Send className="size-2.5 sm:size-3" />
                      Telegram
                      <ExternalLink className="size-2.5 sm:size-3" />
                    </a>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 bg-white/5 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs sm:text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              © {new Date().getFullYear()} <span className="font-semibold text-foreground">{siteTitle}</span>. Tüm hakları saklıdır.
            </div>
          </div>
        </div>
      </footer>

      {/* Page Content Modal */}
      <Dialog open={pageModalOpen} onOpenChange={setPageModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent">
              {pageContent.title}
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            {pageContent.loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="size-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="prose prose-invert max-w-none">
                {pageContent.content.split('\n').map((paragraph, index) => (
                  <p key={index} className="text-muted-foreground leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end">
            <Button variant="glass" onClick={() => setPageModalOpen(false)}>
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Form Modal */}
      <Dialog open={contactModalOpen} onOpenChange={setContactModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent flex items-center gap-2">
              <Mail className="size-5" />
              İletişim
            </DialogTitle>
          </DialogHeader>
          
          {contactSent ? (
            <div className="py-12 text-center">
              <div className="grid size-16 place-items-center rounded-full bg-green-500/20 mx-auto mb-4">
                <CheckCircle className="size-8 text-green-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Mesajınız Gönderildi!</h3>
              <p className="text-muted-foreground mb-6">
                En kısa sürede size dönüş yapacağız.
              </p>
              <Button variant="glass" onClick={() => setContactModalOpen(false)}>
                Kapat
              </Button>
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground">İsim *</label>
                  <Input
                    value={contactForm.firstName}
                    onChange={(e) => setContactForm(p => ({ ...p, firstName: e.target.value }))}
                    className="mt-1 rounded-xl"
                    placeholder="Adınız"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Soyisim</label>
                  <Input
                    value={contactForm.lastName}
                    onChange={(e) => setContactForm(p => ({ ...p, lastName: e.target.value }))}
                    className="mt-1 rounded-xl"
                    placeholder="Soyadınız"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs text-muted-foreground">E-posta *</label>
                <Input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm(p => ({ ...p, email: e.target.value }))}
                  className="mt-1 rounded-xl"
                  placeholder="ornek@email.com"
                />
              </div>
              
              <div>
                <label className="text-xs text-muted-foreground">Mesajınız *</label>
                <Textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm(p => ({ ...p, message: e.target.value }))}
                  className="mt-1 rounded-xl min-h-[120px]"
                  placeholder="Mesajınızı buraya yazın..."
                />
              </div>

              <div className="text-xs text-muted-foreground">
                * işaretli alanlar zorunludur
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setContactModalOpen(false)}>
                  İptal
                </Button>
                <Button 
                  variant="hero" 
                  onClick={submitContact}
                  disabled={contactSending}
                  className="gap-2"
                >
                  {contactSending ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <Send className="size-4" />
                      Gönder
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
